<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import googleAdsAPI from 'dashboard/api/googleAds';
import ga4API from 'dashboard/api/ga4';
import searchConsoleAPI from 'dashboard/api/searchConsole';
import metaAdsAPI from 'dashboard/api/metaAds';
import tiktokAdsAPI from 'dashboard/api/tiktokAds';
import { setupFacebookSdk, launchMetaAdsLogin } from '../utils';

export default {
  components: {
    NextButton,
  },
  data() {
    return {
      connections: [],
      loading: false,
      connectingGoogle: false,
      connectingMeta: false,
      testingId: null,
      customersByConn: {},
      ga4PropsByConn: {},
      scSitesByConn: {},
      loaded: {},
      refreshingKey: '',
      savedId: null,
      popupTimer: null,
      tiktokConnections: [],
      connectingTiktok: false,
      tiktokPopupTimer: null,
      googlePopup: null,
      tiktokPopup: null,
      tiktokAdvertisersByConn: {},
    };
  },
  computed: {
    ...mapGetters({
      metaConnections: 'metaAds/getConnections',
      metaUiFlags: 'metaAds/getUIFlags',
    }),
  },
  mounted() {
    this.fetchConnections();
    this.$store.dispatch('metaAds/get');
    this.fetchTiktokConnections();
    window.addEventListener('storage', this.handleStorageSignal);
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.handleStorageSignal);
    if (this.popupTimer) clearInterval(this.popupTimer);
    if (this.tiktokPopupTimer) clearInterval(this.tiktokPopupTimer);
  },
  methods: {
    // ── Google ──────────────────────────────────────────────
    connectionTitle(conn) {
      return (
        conn.name ||
        conn.customer_id ||
        this.$t('META_ADS.CONNECTIONS.GOOGLE.NEW')
      );
    },
    accountLabel(customer) {
      return customer.name
        ? `${customer.name} (${customer.customer_id})`
        : customer.customer_id;
    },
    propertyLabel(prop) {
      return `${prop.display_name} (${prop.property_id})`;
    },
    isRefreshing(conn, resource) {
      return this.refreshingKey === `${conn.id}-${resource}`;
    },
    // Current stored value as an option, so the select shows the saved choice
    // immediately without waiting for the (slow) live list to load.
    currentOption(conn, resource) {
      if (resource === 'ads') {
        if (!conn.customer_id) return null;
        return {
          value: conn.customer_id,
          label: conn.name
            ? `${conn.name} (${conn.customer_id})`
            : conn.customer_id,
        };
      }
      if (resource === 'ga4') {
        if (!conn.ga4_property_id) return null;
        return {
          value: conn.ga4_property_id,
          label: conn.ga4_property_name
            ? `${conn.ga4_property_name} (${conn.ga4_property_id})`
            : conn.ga4_property_id,
        };
      }
      if (!conn.search_console_site) return null;
      return {
        value: conn.search_console_site,
        label: conn.search_console_site,
      };
    },
    options(conn, resource) {
      let list = [];
      if (resource === 'ads') {
        list = (this.customersByConn[conn.id] || []).map(c => ({
          value: c.customer_id,
          label: this.accountLabel(c),
        }));
      } else if (resource === 'ga4') {
        list = (this.ga4PropsByConn[conn.id] || []).map(p => ({
          value: p.property_id,
          label: this.propertyLabel(p),
        }));
      } else {
        list = (this.scSitesByConn[conn.id] || []).map(s => ({
          value: s.site_url,
          label: s.site_url,
        }));
      }
      const current = this.currentOption(conn, resource);
      if (current && !list.some(o => o.value === current.value)) {
        list.unshift(current);
      }
      return list;
    },
    onGoogleConnected() {
      this.connectingGoogle = false;
      useAlert(this.$t('META_ADS.CONVERSIONS.CONNECTED'));
      this.fetchConnections();
    },
    handleStorageSignal(event) {
      if (!event.newValue) return;
      if (event.key === 'google_ads_oauth') {
        this.closePopup('googlePopup');
        if (event.newValue.startsWith('GOOGLE_ADS_CONNECTED')) {
          this.onGoogleConnected();
        } else {
          this.connectingGoogle = false;
        }
      } else if (event.key === 'tiktok_ads_oauth') {
        this.closePopup('tiktokPopup');
        this.connectingTiktok = false;
        if (event.newValue.startsWith('TIKTOK_ADS_CONNECTED')) {
          this.fetchTiktokConnections();
        }
      }
    },
    closePopup(ref) {
      try {
        if (this[ref] && !this[ref].closed) this[ref].close();
      } catch (error) {
        // popup may already be closed or cross-origin — ignore
      }
      this[ref] = null;
    },
    openOAuthPopup(url) {
      const width = 600;
      const height = 720;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        url,
        'google_ads_oauth',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      this.googlePopup = popup;
      if (this.popupTimer) clearInterval(this.popupTimer);
      this.popupTimer = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(this.popupTimer);
          this.connectingGoogle = false;
          this.fetchConnections();
        }
      }, 800);
    },
    async fetchConnections() {
      this.loading = true;
      try {
        const { data } = await googleAdsAPI.get();
        this.connections = data;
      } finally {
        this.loading = false;
      }
    },
    async reloadConnections() {
      const { data } = await googleAdsAPI.get();
      this.connections = data;
    },
    async connectGoogle() {
      this.connectingGoogle = true;
      try {
        const { data } = await googleAdsAPI.generateAuthorization();
        this.openOAuthPopup(data.url);
      } catch (error) {
        this.connectingGoogle = false;
        useAlert(this.$t('META_ADS.CONVERSIONS.CONNECT_ERROR'));
      }
    },
    async loadCustomers(id) {
      try {
        const { data } = await googleAdsAPI.customers(id);
        this.customersByConn = { ...this.customersByConn, [id]: data };
      } catch (error) {
        this.customersByConn = { ...this.customersByConn, [id]: [] };
      }
    },
    async loadGa4Properties(id) {
      try {
        const { data } = await ga4API.properties(id);
        this.ga4PropsByConn = {
          ...this.ga4PropsByConn,
          [id]: data.data || [],
        };
      } catch (error) {
        this.ga4PropsByConn = { ...this.ga4PropsByConn, [id]: [] };
      }
    },
    async loadScSites(id) {
      try {
        const { data } = await searchConsoleAPI.sites(id);
        this.scSitesByConn = { ...this.scSitesByConn, [id]: data.data || [] };
      } catch (error) {
        this.scSitesByConn = { ...this.scSitesByConn, [id]: [] };
      }
    },
    loadResource(conn, resource) {
      if (resource === 'ads') return this.loadCustomers(conn.id);
      if (resource === 'ga4') return this.loadGa4Properties(conn.id);
      return this.loadScSites(conn.id);
    },
    // Lazy-load the pick list only when the user opens the dropdown — keeps the
    // page instant on mount (no fan-out of slow Google calls per connection).
    ensureLoaded(conn, resource) {
      const key = `${conn.id}-${resource}`;
      if (this.loaded[key]) return;
      this.loaded = { ...this.loaded, [key]: true };
      this.loadResource(conn, resource);
    },
    async refresh(conn, resource) {
      this.refreshingKey = `${conn.id}-${resource}`;
      const key = `${conn.id}-${resource}`;
      this.loaded = { ...this.loaded, [key]: true };
      try {
        await this.loadResource(conn, resource);
      } finally {
        this.refreshingKey = '';
      }
    },
    async persist(conn, payload) {
      // Optimistic: keep the chosen value visible immediately; revert only on error.
      const previous = {
        customer_id: conn.customer_id,
        name: conn.name,
        ga4_property_id: conn.ga4_property_id,
        ga4_property_name: conn.ga4_property_name,
        search_console_site: conn.search_console_site,
      };
      Object.assign(conn, payload);
      try {
        await googleAdsAPI.update(conn.id, payload);
        this.flagSaved(conn.id);
        this.reloadConnections();
      } catch (error) {
        Object.assign(conn, previous);
        useAlert(this.$t('META_ADS.CONVERSIONS.SAVE_ERROR'));
      }
    },
    flagSaved(id) {
      this.savedId = id;
      setTimeout(() => {
        if (this.savedId === id) this.savedId = null;
      }, 2500);
    },
    setCustomer(conn, customerId) {
      const customer = (this.customersByConn[conn.id] || []).find(
        c => c.customer_id === customerId
      );
      this.persist(conn, {
        customer_id: customerId,
        name: (customer && customer.name) || conn.name || '',
      });
    },
    setGa4Property(conn, propertyId) {
      const prop = (this.ga4PropsByConn[conn.id] || []).find(
        p => p.property_id === propertyId
      );
      this.persist(conn, {
        ga4_property_id: propertyId,
        ga4_property_name: (prop && prop.display_name) || '',
      });
    },
    setScSite(conn, siteUrl) {
      this.persist(conn, { search_console_site: siteUrl });
    },
    async testGoogle(conn) {
      this.testingId = conn.id;
      try {
        await googleAdsAPI.test(conn.id);
        useAlert(this.$t('META_ADS.CONVERSIONS.TEST.SUCCESS'));
      } catch (error) {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.error) ||
          '';
        useAlert(
          `${this.$t('META_ADS.CONVERSIONS.TEST.ERROR')} ${message}`.trim()
        );
      } finally {
        this.testingId = null;
      }
    },
    async removeGoogle(id) {
      try {
        await googleAdsAPI.delete(id);
        this.fetchConnections();
      } catch (error) {
        useAlert(this.$t('META_ADS.CONVERSIONS.SAVE_ERROR'));
      }
    },
    // ── Meta ────────────────────────────────────────────────
    metaStatusLabel(status) {
      const labels = {
        active: this.$t('META_ADS.LEAD_ADS.STATUS.ACTIVE'),
        disconnected: this.$t('META_ADS.LEAD_ADS.STATUS.DISCONNECTED'),
        error: this.$t('META_ADS.LEAD_ADS.STATUS.ERROR'),
      };
      return labels[status] || labels.active;
    },
    async connectMeta() {
      const appId = window.chatwootConfig?.metaAdsAppId;
      const configId = window.chatwootConfig?.metaAdsConfigurationId;
      const apiVersion = window.chatwootConfig?.fbApiVersion;
      if (!appId || !configId) {
        this.redirectConnectMeta();
        return;
      }
      this.connectingMeta = true;
      try {
        await setupFacebookSdk(appId, apiVersion);
        const code = await launchMetaAdsLogin(configId);
        await this.$store.dispatch('metaAds/connect', code);
        useAlert(this.$t('META_ADS.LEAD_ADS.AUTHORIZATION.SUCCESS'));
      } catch (error) {
        if (error.message !== 'Login cancelled') {
          useAlert(this.$t('META_ADS.LEAD_ADS.AUTHORIZATION.ERROR'));
        }
      } finally {
        this.connectingMeta = false;
      }
    },
    async redirectConnectMeta() {
      this.connectingMeta = true;
      try {
        const { data } = await metaAdsAPI.generateAuthorization();
        window.location.href = data.url;
      } catch (error) {
        this.connectingMeta = false;
        useAlert(this.$t('META_ADS.LEAD_ADS.AUTHORIZATION.ERROR'));
      }
    },
    async removeMeta(id) {
      try {
        await this.$store.dispatch('metaAds/delete', id);
        useAlert(this.$t('META_ADS.LEAD_ADS.DELETE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('META_ADS.LEAD_ADS.DELETE.ERROR'));
      }
    },
    async fetchTiktokConnections() {
      try {
        const { data } = await tiktokAdsAPI.get();
        this.tiktokConnections = data || [];
        this.tiktokConnections.forEach(c => this.loadTiktokAdvertisers(c.id));
      } catch (error) {
        this.tiktokConnections = [];
      }
    },
    async loadTiktokAdvertisers(id) {
      try {
        const { data } = await tiktokAdsAPI.advertisers(id);
        this.tiktokAdvertisersByConn = {
          ...this.tiktokAdvertisersByConn,
          [id]: data || [],
        };
      } catch (error) {
        this.tiktokAdvertisersByConn = {
          ...this.tiktokAdvertisersByConn,
          [id]: [],
        };
      }
    },
    async onTiktokAdvertiserChange(id, advertiserId) {
      if (!advertiserId) return;
      try {
        await tiktokAdsAPI.update(id, [advertiserId]);
        this.fetchTiktokConnections();
        useAlert(this.$t('META_ADS.CONNECTIONS.TIKTOK.ACCOUNT_SAVED'));
      } catch (error) {
        useAlert(this.$t('META_ADS.CONNECTIONS.TIKTOK.ERROR'));
      }
    },
    async connectTiktok() {
      this.connectingTiktok = true;
      try {
        const { data } = await tiktokAdsAPI.generateAuthorization();
        this.openTiktokPopup(data.url);
      } catch (error) {
        this.connectingTiktok = false;
        useAlert(this.$t('META_ADS.CONNECTIONS.TIKTOK.ERROR'));
      }
    },
    openTiktokPopup(url) {
      const width = 600;
      const height = 720;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        url,
        'tiktok_ads_oauth',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      this.tiktokPopup = popup;
      if (this.tiktokPopupTimer) clearInterval(this.tiktokPopupTimer);
      this.tiktokPopupTimer = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(this.tiktokPopupTimer);
          this.connectingTiktok = false;
          this.fetchTiktokConnections();
        }
      }, 800);
    },
    async removeTiktok(id) {
      try {
        await tiktokAdsAPI.delete(id);
        this.fetchTiktokConnections();
      } catch (error) {
        useAlert(this.$t('META_ADS.CONNECTIONS.TIKTOK.ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full max-w-[60rem] gap-6 mx-auto">
    <div class="min-w-0">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ $t('META_ADS.CONNECTIONS.HEADER') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-10">
        {{ $t('META_ADS.CONNECTIONS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Google -->
    <div class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2">
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-2 min-w-0">
          <span class="i-lucide-chrome text-n-slate-11 text-lg shrink-0" />
          <div class="min-w-0">
            <h2 class="text-base font-medium text-n-slate-12">
              {{ $t('META_ADS.CONNECTIONS.GOOGLE.TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-10">
              {{ $t('META_ADS.CONNECTIONS.GOOGLE.ENABLES') }}
            </p>
          </div>
        </div>
        <NextButton
          icon="i-lucide-circle-plus"
          :label="$t('META_ADS.CONNECTIONS.GOOGLE.CONNECT')"
          :is-loading="connectingGoogle"
          sm
          class="shrink-0"
          @click="connectGoogle"
        />
      </div>

      <div
        v-if="!loading && !connections.length"
        class="p-4 text-sm text-center rounded-lg text-n-slate-11 bg-n-alpha-black2"
      >
        {{ $t('META_ADS.CONNECTIONS.GOOGLE.EMPTY') }}
      </div>

      <div
        v-for="conn in connections"
        :key="conn.id"
        class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-black2"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex items-center gap-2 min-w-0">
            <p class="font-medium truncate text-n-slate-12">
              {{ connectionTitle(conn) }}
            </p>
            <span
              v-if="savedId === conn.id"
              class="inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400 shrink-0"
            >
              <span class="i-lucide-check" />
              {{ $t('META_ADS.CONNECTIONS.GOOGLE.SAVED') }}
            </span>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <NextButton
              v-if="conn.customer_id"
              icon="i-lucide-badge-check"
              :is-loading="testingId === conn.id"
              slate
              faded
              xs
              @click="testGoogle(conn)"
            />
            <NextButton
              icon="i-lucide-trash-2"
              ruby
              faded
              xs
              @click="removeGoogle(conn.id)"
            />
          </div>
        </div>

        <!-- Account / property / site selectors (editable anytime, refreshable) -->
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <!-- Google Ads account -->
          <div class="flex flex-col gap-1">
            <div class="flex items-center justify-between">
              <span class="text-xs font-medium text-n-slate-11">
                {{ $t('META_ADS.CONNECTIONS.GOOGLE.ADS_ACCOUNT') }}
              </span>
              <button
                type="button"
                class="text-n-slate-10 hover:text-n-slate-12"
                :title="$t('META_ADS.CONNECTIONS.GOOGLE.REFRESH')"
                @click="refresh(conn, 'ads')"
              >
                <span
                  class="i-lucide-refresh-cw text-xs"
                  :class="{ 'animate-spin': isRefreshing(conn, 'ads') }"
                />
              </button>
            </div>
            <select
              class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
              :value="conn.customer_id || ''"
              @focus="ensureLoaded(conn, 'ads')"
              @change="setCustomer(conn, $event.target.value)"
            >
              <option value="">
                {{ $t('META_ADS.CONNECTIONS.GOOGLE.NOT_SELECTED') }}
              </option>
              <option
                v-for="o in options(conn, 'ads')"
                :key="o.value"
                :value="o.value"
              >
                {{ o.label }}
              </option>
            </select>
          </div>

          <!-- GA4 property -->
          <div class="flex flex-col gap-1">
            <div class="flex items-center justify-between">
              <span class="text-xs font-medium text-n-slate-11">
                {{ $t('META_ADS.CONNECTIONS.GOOGLE.GA4_PROPERTY') }}
              </span>
              <button
                type="button"
                class="text-n-slate-10 hover:text-n-slate-12"
                :title="$t('META_ADS.CONNECTIONS.GOOGLE.REFRESH')"
                @click="refresh(conn, 'ga4')"
              >
                <span
                  class="i-lucide-refresh-cw text-xs"
                  :class="{ 'animate-spin': isRefreshing(conn, 'ga4') }"
                />
              </button>
            </div>
            <select
              class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
              :value="conn.ga4_property_id || ''"
              @focus="ensureLoaded(conn, 'ga4')"
              @change="setGa4Property(conn, $event.target.value)"
            >
              <option value="">
                {{ $t('META_ADS.CONNECTIONS.GOOGLE.NOT_SELECTED') }}
              </option>
              <option
                v-for="o in options(conn, 'ga4')"
                :key="o.value"
                :value="o.value"
              >
                {{ o.label }}
              </option>
            </select>
          </div>

          <!-- Search Console site -->
          <div class="flex flex-col gap-1">
            <div class="flex items-center justify-between">
              <span class="text-xs font-medium text-n-slate-11">
                {{ $t('META_ADS.CONNECTIONS.GOOGLE.SC_SITE') }}
              </span>
              <button
                type="button"
                class="text-n-slate-10 hover:text-n-slate-12"
                :title="$t('META_ADS.CONNECTIONS.GOOGLE.REFRESH')"
                @click="refresh(conn, 'sc')"
              >
                <span
                  class="i-lucide-refresh-cw text-xs"
                  :class="{ 'animate-spin': isRefreshing(conn, 'sc') }"
                />
              </button>
            </div>
            <select
              class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
              :value="conn.search_console_site || ''"
              @focus="ensureLoaded(conn, 'sc')"
              @change="setScSite(conn, $event.target.value)"
            >
              <option value="">
                {{ $t('META_ADS.CONNECTIONS.GOOGLE.NOT_SELECTED') }}
              </option>
              <option
                v-for="o in options(conn, 'sc')"
                :key="o.value"
                :value="o.value"
              >
                {{ o.label }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Meta -->
    <div class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2">
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-2 min-w-0">
          <span class="i-lucide-facebook text-n-slate-11 text-lg shrink-0" />
          <div class="min-w-0">
            <h2 class="text-base font-medium text-n-slate-12">
              {{ $t('META_ADS.CONNECTIONS.META.TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-10">
              {{ $t('META_ADS.CONNECTIONS.META.ENABLES') }}
            </p>
          </div>
        </div>
        <NextButton
          icon="i-lucide-circle-plus"
          :label="$t('META_ADS.CONNECTIONS.META.CONNECT')"
          :is-loading="connectingMeta"
          sm
          class="shrink-0"
          @click="connectMeta"
        />
      </div>

      <div
        v-if="!metaUiFlags.fetchingList && !metaConnections.length"
        class="p-4 text-sm text-center rounded-lg text-n-slate-11 bg-n-alpha-black2"
      >
        {{ $t('META_ADS.CONNECTIONS.META.EMPTY') }}
      </div>

      <div
        v-for="conn in metaConnections"
        :key="conn.id"
        class="flex items-center justify-between gap-3 p-4 rounded-lg bg-n-alpha-black2"
      >
        <div class="min-w-0">
          <p class="font-medium truncate text-n-slate-12">{{ conn.name }}</p>
          <p class="text-xs text-n-slate-10">
            {{ $t('META_ADS.LEAD_ADS.LIST.PAGE_ID', { id: conn.page_id }) }}
          </p>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <span
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            :class="
              conn.status === 'active'
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            "
          >
            {{ metaStatusLabel(conn.status) }}
          </span>
          <NextButton
            icon="i-lucide-trash-2"
            ruby
            faded
            xs
            @click="removeMeta(conn.id)"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2">
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-2 min-w-0">
          <span class="i-lucide-music text-n-slate-11 text-lg shrink-0" />
          <div class="min-w-0">
            <h2 class="text-base font-medium text-n-slate-12">
              {{ $t('META_ADS.CONNECTIONS.TIKTOK.TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-10">
              {{ $t('META_ADS.CONNECTIONS.TIKTOK.ENABLES') }}
            </p>
          </div>
        </div>
        <NextButton
          icon="i-lucide-circle-plus"
          :label="$t('META_ADS.CONNECTIONS.TIKTOK.CONNECT')"
          :is-loading="connectingTiktok"
          sm
          class="shrink-0"
          @click="connectTiktok"
        />
      </div>

      <div
        v-if="!tiktokConnections.length"
        class="p-4 text-sm text-center rounded-lg text-n-slate-11 bg-n-alpha-black2"
      >
        {{ $t('META_ADS.CONNECTIONS.TIKTOK.EMPTY') }}
      </div>

      <div
        v-for="conn in tiktokConnections"
        :key="conn.id"
        class="flex items-center justify-between gap-3 p-4 rounded-lg bg-n-alpha-black2"
      >
        <div class="min-w-0">
          <p class="font-medium truncate text-n-slate-12">{{ conn.name }}</p>
          <div class="flex items-center gap-2 mt-1">
            <label class="text-xs text-n-slate-10 shrink-0">
              {{ $t('META_ADS.CONNECTIONS.TIKTOK.ACCOUNT') }}
            </label>
            <select
              v-if="(tiktokAdvertisersByConn[conn.id] || []).length"
              :value="(conn.advertiser_ids || [])[0] || ''"
              class="px-2 py-1 text-xs rounded border border-n-weak bg-n-solid-1 text-n-slate-12 max-w-[16rem] truncate"
              @change="onTiktokAdvertiserChange(conn.id, $event.target.value)"
            >
              <option
                v-for="a in tiktokAdvertisersByConn[conn.id]"
                :key="a.id"
                :value="a.id"
              >
                {{ a.name }}
              </option>
            </select>
            <span v-else class="text-xs text-n-slate-10">
              {{
                $t('META_ADS.CONNECTIONS.TIKTOK.ADVERTISERS', {
                  count: conn.advertiser_count,
                })
              }}
            </span>
          </div>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <span
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            :class="
              conn.status === 'active'
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            "
          >
            {{ metaStatusLabel(conn.status) }}
          </span>
          <NextButton
            icon="i-lucide-trash-2"
            ruby
            faded
            xs
            @click="removeTiktok(conn.id)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
